import { Download, FileText, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Document } from '@/hooks/useDocuments';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DocumentListProps {
  documents: Document[];
  onDelete?: (documentId: string) => void;
  isDeleting?: boolean;
}

export const DocumentList = ({ documents, onDelete, isDeleting }: DocumentListProps) => {
  const { user } = useAuth();

  const handleDownload = async (doc: Document) => {
    try {
      // Extract storage path from URL
      const urlParts = doc.file_url.split('/delivery_documents/');
      if (urlParts.length > 1) {
        const storagePath = urlParts[1];
        const { data, error } = await supabase.storage
          .from('delivery_documents')
          .download(storagePath);

        if (error) throw error;

        // Create download link
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const getFileTypeColor = (type: string) => {
    if (type.startsWith('image/')) return 'bg-[#0880AE]/10 text-[#0880AE]';
    if (type === 'application/pdf') return 'bg-[#FF7171]/10 text-[#FF7171]';
    return 'bg-[#87ADD4]/10 text-[#5A6B7A]';
  };

  const getFileTypeLabel = (type: string) => {
    if (type.startsWith('image/')) return 'Image';
    if (type === 'application/pdf') return 'PDF';
    return 'Fichier';
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 bg-white rounded-xl border border-[#DBE2EA]">
        <FileText className="mx-auto h-12 w-12 text-[#87ADD4] mb-4" />
        <p className="text-[#5A6B7A] font-['Funnel_Sans']">
          Aucun document disponible
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {documents.map((doc) => (
        <Card key={doc.id} className="p-4 border border-[#DBE2EA] hover:border-[#0880AE] transition-colors">
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            <div className="w-16 h-16 bg-[#EBF4F8] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              {doc.file_type.startsWith('image/') ? (
                <img
                  src={doc.file_url}
                  alt={doc.file_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileText size={28} className="text-[#0880AE]" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold text-[#2D3748] font-['Funnel_Sans'] truncate">
                  {doc.file_name}
                </h4>
                <Badge className={getFileTypeColor(doc.file_type)}>
                  {getFileTypeLabel(doc.file_type)}
                </Badge>
              </div>

              <div className="space-y-1 text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                <p>
                  Uploadé le {format(new Date(doc.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </p>
                {doc.uploader && (
                  <p>
                    Par {doc.uploader.first_name} {doc.uploader.last_name || doc.uploader.email}
                  </p>
                )}
                <p>
                  {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => handleDownload(doc)}
                  size="sm"
                  className="bg-[#37699E] hover:bg-[#2E5782] text-white font-semibold"
                >
                  <Download size={14} className="mr-1" />
                  Télécharger
                </Button>
                {onDelete && user?.id === doc.uploaded_by && (
                  <Button
                    onClick={() => onDelete(doc.id)}
                    disabled={isDeleting}
                    size="sm"
                    variant="ghost"
                    className="text-[#FF7171] hover:text-[#E65959] hover:bg-[#FF7171]/10"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
